/**
 * Counts the AudioContexts alive on the page and reports every one created,
 * every one closed, and every constructor that throws.
 *
 * `AudioContextController` only sees the shared context. Libraries build their
 * own behind it: livekit's `useTrackVolume` and `createAudioAnalyser` make one
 * per call, and its `detectSilence` makes one every time a mic track is
 * created or published. The only way to count those is to wrap the global
 * constructor, so `install` replaces `window.AudioContext` with a counting
 * proxy before anything else can build a context.
 *
 * Why: a Safari student lost all peer audio after every slide change (room
 * OqJJx9Kz02A2392spx7f) while every audio element reported itself playing.
 * Safari caps live contexts per page; this census records how many the page
 * actually holds over a session.
 */

type CensusCounts = {
  /** Constructed and not yet closed, after this event. */
  liveCount: number
  /** Every context constructed since install, closed or not. */
  totalCreated: number
}

export type AudioContextCensusEvent =
  // `contextId` is the context's creation number on this page (1, 2, 3...),
  // so a `created` and its `closed` can be paired.
  | ({ type: 'created'; contextId: number } & CensusCounts)
  | ({ type: 'closed'; contextId: number } & CensusCounts)
  // The raw error, so the logger can run it through the telemetry sanitizer.
  | ({ type: 'create_failed'; error: unknown } & CensusCounts)

type AudioContextCensusListener = (event: AudioContextCensusEvent) => void

/** All the census reads from a context it counted. */
type CountedContext = Pick<
  AudioContext,
  'state' | 'addEventListener' | 'removeEventListener'
>

type AudioContextConstructor = new (
  options?: AudioContextOptions
) => CountedContext

/** The two globals a browser may expose the constructor under. */
type AudioContextHost = {
  AudioContext?: AudioContextConstructor
  webkitAudioContext?: AudioContextConstructor
}

export class AudioContextCensus {
  liveCount = 0
  totalCreated = 0

  private installed = false
  private listeners = new Set<AudioContextCensusListener>()

  /**
   * Replace the host's AudioContext constructors with counting proxies. Call
   * once at app start; later calls do nothing, so a hot reload doesn't wrap
   * the proxy again and count every context twice.
   */
  install(host: AudioContextHost) {
    if (this.installed) return
    this.installed = true

    // livekit falls back to the prefixed name when the plain one is missing,
    // so whichever globals exist get wrapped.
    if (host.AudioContext) host.AudioContext = this.wrap(host.AudioContext)
    if (host.webkitAudioContext) {
      host.webkitAudioContext = this.wrap(host.webkitAudioContext)
    }
  }

  /** Observe created, closed and failed contexts. Returns an unsubscribe. */
  subscribe(listener: AudioContextCensusListener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  // A Proxy rather than a subclass: `instanceof AudioContext`, static members
  // and anyone subclassing the global keep working, because `Reflect.construct`
  // passes `newTarget` through.
  private wrap<Constructor extends AudioContextConstructor>(
    native: Constructor
  ): Constructor {
    return new Proxy(native, {
      construct: (target, args, newTarget) =>
        this.construct(() => Reflect.construct(target, args, newTarget)),
    })
  }

  private construct(build: () => CountedContext): CountedContext {
    let context: CountedContext
    try {
      context = build()
    } catch (e) {
      this.emit({ type: 'create_failed', error: e, ...this.counts() })
      throw e
    }

    this.totalCreated++
    this.liveCount++
    const contextId = this.totalCreated

    // `close()` moves the context to 'closed' and fires statechange; that is
    // the only point a context stops counting against the browser's limit.
    const onStateChange = () => {
      if (context.state !== 'closed') return
      context.removeEventListener('statechange', onStateChange)
      this.liveCount--
      this.emit({ type: 'closed', contextId, ...this.counts() })
    }
    context.addEventListener('statechange', onStateChange)

    this.emit({ type: 'created', contextId, ...this.counts() })
    return context
  }

  private counts(): CensusCounts {
    return { liveCount: this.liveCount, totalCreated: this.totalCreated }
  }

  // Runs inside the AudioContext constructor. A listener that throws must not
  // turn a successful construction into an error (the caller would never get
  // the context back to close it) or replace the browser's own error.
  private emit(event: AudioContextCensusEvent) {
    for (const listener of this.listeners) {
      try {
        listener(event)
      } catch (e) {
        console.warn('audioContextCensus: listener threw', e)
      }
    }
  }
}

export const audioContextCensus = new AudioContextCensus()
